/**
 * MonitorConnectPage — `/monitor/connect`. Public, no auth, no shell.
 *
 * "Connect your business." Shows the connector catalog as cards (most are
 * honestly marked "one-click — coming soon"), AND a genuinely working path
 * today: a "Send your data" panel that submits a sample batch (prefilled to
 * trip a Critical insight) to the ingest endpoint with the saved token, then
 * shows the returned plain-language insight + severity. The ingest URL +
 * a curl example let a client wire their own systems immediately.
 *
 * Redirects to `/monitor/start` when no token is saved.
 */

import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { GiOrbitLogo } from '@/components/GiOrbitLogo';
import {
  getMonitorToken,
  getIngestUrl,
  monitorUrl,
  ingest,
  MonitorError,
  type IngestResult,
} from './monitorClient';

const ink = '#111827';
const inkMuted = '#4b5563';
const inkSubtle = '#6b7280';
const rim = '#e5e7eb';
const accent = '#1d4ed8';

const SEVERITY_STYLE: Record<string, { fg: string; bg: string; border: string; label: string }> = {
  ok: { fg: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', label: 'Looking good' },
  warning: { fg: '#b45309', bg: '#fffbeb', border: '#fde68a', label: 'Worth a look' },
  critical: { fg: '#b91c1c', bg: '#fef2f2', border: '#fecaca', label: 'Needs attention' },
};

function severityStyle(s: string) {
  return SEVERITY_STYLE[s] ?? { fg: '#374151', bg: '#f9fafb', border: '#e5e7eb', label: s };
}

interface ConnectorCard {
  name: string;
  blurb: string;
}

const CONNECTORS: ConnectorCard[] = [
  { name: 'Email', blurb: 'Spot deliverability and cost drift in your sending.' },
  { name: 'Stripe', blurb: 'Catch failed charges, refunds, and revenue leaks.' },
  { name: 'Google Analytics', blurb: 'See where traffic and conversions slip.' },
  { name: 'Slack', blurb: 'Turn alerts into plain-language findings.' },
  { name: 'Shopify', blurb: 'Watch orders, fees, and fulfillment costs.' },
  { name: 'Any tool with an API', blurb: 'Send us your numbers — we do the rest.' },
];

/**
 * A realistic sample batch that trips a Critical finding (a CPU at 97% on a
 * paid host = money burning). OTLP-shaped JSON the client can edit before
 * sending. Prefilled so the screen works on the very first click.
 */
const SAMPLE_OTLP = JSON.stringify(
  {
    resourceMetrics: [
      {
        resource: {
          attributes: [
            { key: 'service.name', value: { stringValue: 'checkout-api' } },
            { key: 'host.name', value: { stringValue: 'prod-web-01' } },
          ],
        },
        scopeMetrics: [
          {
            metrics: [
              {
                name: 'system.cpu.utilization',
                gauge: {
                  dataPoints: [
                    {
                      asDouble: 0.97,
                      timeUnixNano: '1718000000000000000',
                      attributes: [{ key: 'state', value: { stringValue: 'used' } }],
                    },
                  ],
                },
              },
            ],
          },
        ],
      },
    ],
  },
  null,
  2,
);

export function MonitorConnectPage() {
  const navigate = useNavigate();
  const token = getMonitorToken();
  if (!token) return <Navigate to="/monitor/start" replace />;

  const ingestEndpoint = getIngestUrl() ?? monitorUrl('/api/v1/client/monitor/ingest');

  const [draft, setDraft] = useState(SAMPLE_OTLP);
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [error, setError] = useState('');
  const [result, setResult] = useState<IngestResult | null>(null);
  const [copied, setCopied] = useState(false);

  const curl = useMemo(
    () =>
      [
        `curl -X POST ${ingestEndpoint} \\`,
        `  -H "Authorization: Bearer ${token}" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d @your-data.json`,
      ].join('\n'),
    [ingestEndpoint, token],
  );

  async function handleSend() {
    setStatus('sending');
    setError('');
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft);
    } catch {
      setError('That is not valid JSON. Check for a missing comma or bracket and try again.');
      setStatus('error');
      return;
    }
    try {
      const res = await ingest(token!, parsed);
      setResult(res);
      setStatus('done');
    } catch (err) {
      setError(
        err instanceof MonitorError
          ? err.message
          : 'We could not reach your workspace. Please try again in a moment.',
      );
      setStatus('error');
    }
  }

  async function copyCurl() {
    try {
      await navigator.clipboard.writeText(curl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* visible to copy by hand */
    }
  }

  const sev = result ? severityStyle(result.severity) : null;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#ffffff',
        color: ink,
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '18px 24px',
          borderBottom: `1px solid ${rim}`,
        }}
      >
        <GiOrbitLogo size="sm" />
        <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>Growing Intelligence</span>
        <button
          type="button"
          onClick={() => navigate('/monitor/dashboard')}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: accent,
            background: '#ffffff',
            border: `1px solid ${rim}`,
            borderRadius: 6,
            padding: '7px 14px',
            cursor: 'pointer',
          }}
        >
          Go to dashboard →
        </button>
      </header>

      <main style={{ maxWidth: 880, margin: '0 auto', padding: '32px 24px 56px' }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 10px', lineHeight: 1.15 }}>
          Connect your business.
        </h1>
        <p style={{ fontSize: 16, color: inkMuted, margin: '0 0 28px', maxWidth: 640, lineHeight: 1.55 }}>
          Point Monitor at the tools you already use. One-click connectors are rolling out — and you
          can send your data today to see your first finding right now.
        </p>

        {/* Connector catalog */}
        <section style={{ marginBottom: 40 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 14px' }}>Your tools</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 14,
            }}
          >
            {CONNECTORS.map((c) => (
              <div
                key={c.name}
                style={{
                  border: `1px solid ${rim}`,
                  borderRadius: 10,
                  padding: '16px 18px',
                  background: '#ffffff',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{c.name}</span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: inkSubtle,
                      background: '#f3f4f6',
                      border: `1px solid ${rim}`,
                      borderRadius: 999,
                      padding: '2px 8px',
                    }}
                  >
                    Coming soon
                  </span>
                </div>
                <p style={{ fontSize: 13, color: inkMuted, margin: 0, lineHeight: 1.5 }}>{c.blurb}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: inkSubtle, margin: '12px 0 0' }}>
            One-click connectors are honestly still in the works. The panel below works right now.
          </p>
        </section>

        {/* Working "Send your data" panel */}
        <section
          style={{
            border: `1px solid ${rim}`,
            borderRadius: 12,
            padding: '22px 22px 24px',
            background: '#ffffff',
            marginBottom: 36,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>Send your data</h2>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#15803d',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: 999,
                padding: '2px 8px',
              }}
            >
              Works now
            </span>
          </div>
          <p style={{ fontSize: 13, color: inkMuted, margin: '0 0 14px', lineHeight: 1.5 }}>
            We've prefilled a realistic sample below. Send it as-is to see a finding, or paste your
            own numbers. Nothing is shared — this goes only to your workspace.
          </p>

          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            rows={12}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              fontSize: 12.5,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              lineHeight: 1.5,
              padding: '12px 14px',
              border: `1px solid ${rim}`,
              borderRadius: 8,
              outline: 'none',
              resize: 'vertical',
              color: ink,
              background: '#fbfbfd',
            }}
          />

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginTop: 14 }}>
            <button
              type="button"
              onClick={handleSend}
              disabled={status === 'sending'}
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: '#ffffff',
                background: status === 'sending' ? '#9ca3af' : accent,
                border: 'none',
                borderRadius: 8,
                padding: '11px 20px',
                cursor: status === 'sending' ? 'default' : 'pointer',
              }}
            >
              {status === 'sending' ? 'Checking your data…' : 'Send & see my first finding'}
            </button>
            <button
              type="button"
              onClick={() => setDraft(SAMPLE_OTLP)}
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: inkMuted,
                background: '#ffffff',
                border: `1px solid ${rim}`,
                borderRadius: 8,
                padding: '11px 16px',
                cursor: 'pointer',
              }}
            >
              Reset sample
            </button>
          </div>

          {status === 'error' && (
            <p style={{ fontSize: 13, color: '#b91c1c', margin: '14px 0 0' }}>{error}</p>
          )}

          {status === 'done' && result && sev && (
            <div
              style={{
                marginTop: 18,
                border: `1px solid ${sev.border}`,
                background: sev.bg,
                borderRadius: 10,
                padding: '16px 18px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: sev.fg,
                    background: '#ffffff',
                    border: `1px solid ${sev.border}`,
                    borderRadius: 999,
                    padding: '2px 10px',
                  }}
                >
                  {sev.label}
                </span>
                <span style={{ fontSize: 12, color: inkSubtle }}>
                  {result.stored} reading{result.stored !== 1 ? 's' : ''} checked · signed
                </span>
              </div>
              <p style={{ fontSize: 15, fontWeight: 600, color: ink, margin: '0 0 10px', lineHeight: 1.5 }}>
                {result.insight}
              </p>
              <button
                type="button"
                onClick={() => navigate('/monitor/dashboard')}
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#ffffff',
                  background: accent,
                  border: 'none',
                  borderRadius: 8,
                  padding: '9px 16px',
                  cursor: 'pointer',
                }}
              >
                See all my findings →
              </button>
            </div>
          )}
        </section>

        {/* Wire-your-own (curl) */}
        <section>
          <h2 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px' }}>
            Wire your own systems
          </h2>
          <p style={{ fontSize: 13, color: inkMuted, margin: '0 0 12px', lineHeight: 1.5 }}>
            Send your data from anywhere using your access key. Here's the exact command:
          </p>
          <div
            style={{
              position: 'relative',
              border: `1px solid ${rim}`,
              borderRadius: 10,
              background: '#0f172a',
              padding: '14px 16px',
            }}
          >
            <button
              type="button"
              onClick={copyCurl}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                fontSize: 11,
                fontWeight: 600,
                color: '#e2e8f0',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 6,
                padding: '4px 10px',
                cursor: 'pointer',
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            <pre
              style={{
                margin: 0,
                fontSize: 12.5,
                lineHeight: 1.6,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                color: '#e2e8f0',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {curl}
            </pre>
          </div>
          <p style={{ fontSize: 12, color: inkSubtle, margin: '20px 0 0', lineHeight: 1.5 }}>
            Your data stays yours. Every result is signed — verify it yourself, anytime.
          </p>
        </section>
      </main>
    </div>
  );
}

export default MonitorConnectPage;
